import PropTypes from 'prop-types'

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { getTestId } from '@shared/utils'

import LinearProgress from '@components/LinearProgress'

ConfirmationV2.propTypes = {
  /** Whether the modal is shown */
  open: PropTypes.bool.isRequired,

  /** Title of the dialog */
  title: PropTypes.string.isRequired,

  /** Description of the dialog */
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /** Variant of the dialog */
  variant: PropTypes.oneOf(['success', 'error', 'warning', 'info']),

  /** Label of 'reject' button */
  rejectLabel: PropTypes.string,

  /** Label of 'confirm' button */
  confirmLabel: PropTypes.string,

  /** Called after the reject button is clicked */
  onReject: PropTypes.func.isRequired,

  /** Called after the confirm button is clicked */
  onConfirm: PropTypes.func.isRequired,

  /** Can show linear progress on top */
  loading: PropTypes.bool,

  /** Hide the reject button */
  hideReject: PropTypes.bool,

  /** Disable the confirm button */
  confirmDisabled: PropTypes.bool,
}

export default function ConfirmationV2({
  open,
  title,
  description,
  variant = 'info',
  rejectLabel = 'Cancel',
  confirmLabel = 'Confirm',
  onReject,
  onConfirm,
  loading = false,
  hideReject = false,
  confirmDisabled = false,
  preventEasyDismiss = false,
  maxWidth = 'xs',
  ...rest
}) {
  const testId = getTestId(rest, 'confirmation-v2-dialog')
  const config = VARIANT_CONFIG[variant]
  const IconComponent = config.icon

  return (
    <Dialog
      open={open}
      {...(preventEasyDismiss ? {} : { onClose: onReject })}
      fullWidth
      maxWidth={maxWidth}
      slotProps={{
        paper: {
          'data-testid': testId,
          sx: { borderRadius: 2, textAlign: 'center' },
        },
      }}
    >
      <LinearProgress loading={loading} />
      <DialogContent sx={{ p: 4 }}>
        <Stack spacing={2} sx={{ alignItems: 'center' }}>
          <Avatar sx={{ width: 64, height: 64, backgroundColor: config.bgColor }}>
            <IconComponent sx={{ fontSize: 32, color: config.color }} />
          </Avatar>

          <Typography variant="h5" data-testid={`${testId}-title`}>
            {title}
          </Typography>

          {description && (
            <>
              {typeof description === 'string' ? (
                <Typography data-testid={`${testId}-description`} sx={{ whiteSpace: 'pre-line', color: 'text.secondary', maxWidth: 400 }}>
                  {description}
                </Typography>
              ) : (
                description
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 1 }}>
        {!hideReject && (
          <Button fullWidth onClick={onReject} variant="outlined" data-testid={`${testId}-reject`} disabled={loading}>
            {rejectLabel}
          </Button>
        )}
        <Button
          fullWidth
          autoFocus
          color={variant}
          onClick={onConfirm}
          variant="contained"
          data-testid={`${testId}-confirm`}
          loading={loading}
          disabled={confirmDisabled || loading}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const VARIANT_CONFIG = {
  success: {
    icon: CheckCircleOutlineIcon,
    color: 'success.main',
    bgColor: 'success.lighter',
  },
  error: {
    icon: ErrorOutlineIcon,
    color: 'error.main',
    bgColor: 'error.lighter',
  },
  warning: {
    icon: WarningAmberIcon,
    color: 'warning.main',
    bgColor: 'warning.lighter',
  },
  info: {
    icon: InfoOutlinedIcon,
    color: 'info.main',
    bgColor: 'info.lighter',
  },
}
